import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, message } from 'antd';
import { AudioOutlined } from '@ant-design/icons';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../stores/authStore';
import './VoiceInputButton.css';

type VoiceStatus = 'idle' | 'connecting' | 'listening' | 'stopping' | 'error';

interface VoiceInputButtonProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  size?: 'small' | 'normal';
  title?: string;
}

async function float32ToPcm16k(input: Float32Array, inputRate: number) {
  const ratio = inputRate / 16000;
  const length = Math.max(1, Math.floor(input.length / ratio));
  const pcm = new Int16Array(length);
  for (let i = 0; i < length; i += 1) {
    const sample = input[Math.floor(i * ratio)] || 0;
    const clipped = Math.max(-1, Math.min(1, sample));
    pcm[i] = clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff;
  }
  return pcm;
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return window.btoa(binary);
}

function joinInput(base: string, spoken: string) {
  if (!spoken.trim()) return base || '';
  if (!base.trim()) return spoken.trim();
  const separator = /[\n\s]$/.test(base) ? '' : '\n';
  return `${base}${separator}${spoken.trim()}`;
}

export default function VoiceInputButton({ value, onChange, disabled, className = '', size = 'normal', title }: VoiceInputButtonProps) {
  const authUser = useAuthStore(state => state.user);
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const socketRef = useRef<Socket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const baseValueRef = useRef('');
  const finalTextRef = useRef('');
  const partialTextRef = useRef('');
  const onChangeRef = useRef(onChange);

  const isRecording = status === 'connecting' || status === 'listening' || status === 'stopping';

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const cleanupAudio = useCallback(() => {
    try { processorRef.current?.disconnect(); } catch {}
    processorRef.current = null;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
  }, []);

  const stop = useCallback(() => {
    if (!socketRef.current && !streamRef.current) {
      setStatus('idle');
      return;
    }
    setStatus('stopping');
    cleanupAudio();
    try { socketRef.current?.emit('ai-demand:asr-stop'); } catch {}
    socketRef.current?.disconnect();
    socketRef.current = null;
    setStatus('idle');
  }, [cleanupAudio]);

  const start = useCallback(async () => {
    if (disabled) return;
    if (!authUser?.id) {
      message.warning('请先登录后再使用语音输入');
      return;
    }
    if (!navigator?.mediaDevices?.getUserMedia) {
      message.error('当前浏览器不支持麦克风，或当前访问方式不是安全上下文；请使用 HTTPS 域名访问');
      setStatus('error');
      return;
    }

    setStatus('connecting');
    baseValueRef.current = value || '';
    finalTextRef.current = '';
    partialTextRef.current = '';

    try {
      const socket = io('/ws', { auth: { token: useAuthStore.getState().token } });
      socketRef.current = socket;

      socket.on('ai-demand:asr-ready', () => setStatus('listening'));
      socket.on('ai-demand:asr-text', (payload: { text?: string; isFinal?: boolean }) => {
        const text = payload?.text?.trim();
        if (!text) return;
        if (payload.isFinal) {
          if (!finalTextRef.current.endsWith(text)) {
            finalTextRef.current = `${finalTextRef.current ? `${finalTextRef.current}\n` : ''}${text}`;
          }
          partialTextRef.current = '';
        } else {
          partialTextRef.current = text;
        }
        const spoken = `${finalTextRef.current}${finalTextRef.current && partialTextRef.current ? '\n' : ''}${partialTextRef.current}`.trim();
        onChangeRef.current(joinInput(baseValueRef.current, spoken));
      });
      socket.on('ai-demand:asr-error', (payload: { message?: string }) => {
        const msg = payload?.message || '实时转写失败';
        setStatus('error');
        message.error(msg);
      });
      socket.on('connect_error', (err: any) => {
        const msg = err?.message || '转写服务连接失败';
        setStatus('error');
        message.error(msg);
      });
      socket.on('disconnect', () => {
        setStatus(prev => (prev === 'connecting' || prev === 'listening' ? 'idle' : prev));
      });

      socket.emit('ai-demand:asr-start');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, noiseSuppression: true, echoCancellation: true } });
      streamRef.current = stream;
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextCtor();
      audioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') await audioContext.resume();
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      processor.onaudioprocess = async event => {
        const socketNow = socketRef.current;
        if (!socketNow?.connected) return;
        const pcm = await float32ToPcm16k(event.inputBuffer.getChannelData(0), audioContext.sampleRate);
        socketNow.emit('ai-demand:asr-audio', { audio: arrayBufferToBase64(pcm.buffer) });
      };
      source.connect(processor);
      processor.connect(audioContext.destination);
    } catch (error: any) {
      cleanupAudio();
      try { socketRef.current?.emit('ai-demand:asr-stop'); } catch {}
      socketRef.current?.disconnect();
      socketRef.current = null;
      const msg = error?.name === 'NotAllowedError'
        ? '麦克风权限被拒绝，请在浏览器地址栏允许麦克风访问'
        : (error?.message || '无法启动语音输入');
      setStatus('error');
      message.error(msg);
    }
  }, [authUser?.id, cleanupAudio, disabled, value]);

  useEffect(() => {
    return () => {
      cleanupAudio();
      try { socketRef.current?.emit('ai-demand:asr-stop'); } catch {}
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [cleanupAudio]);

  const label = status === 'connecting'
    ? '正在连接转写服务…'
    : status === 'listening'
      ? '正在语音输入，点击停止'
      : status === 'stopping'
        ? '正在停止语音输入…'
        : title || '语音输入';

  return (
    <Tooltip title={label}>
      <button
        type="button"
        className={`voice-input-button voice-input-button-${size} ${isRecording ? 'is-recording' : ''} ${status === 'error' ? 'is-error' : ''} ${className}`}
        onMouseDown={e => e.preventDefault()}
        onClick={isRecording ? stop : start}
        disabled={disabled && !isRecording}
        aria-label={label}
      >
        <AudioOutlined spin={isRecording} />
        <span>{isRecording ? '停止' : '语音'}</span>
      </button>
    </Tooltip>
  );
}
